import type { RssArticle, RssPlatform } from '../types/rss'
import { optimizeThumbnail } from './image'

const ITEMS_PER_FEED = 2

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim()
}

function extractThumbnail(content: string): string | undefined {
  const imgMatch = content.match(/<img[^>]+src=["']([^"']+)["']/)
  return optimizeThumbnail(imgMatch?.[1])
}

// Collect feed tags from <category> elements. RSS 2.0 keeps the name as text
// content (Zenn topics are backfilled this way in api/rss.ts); Atom uses a
// `term` attribute. Returns undefined when the item carries no tags.
function extractTags(item: Element): string[] | undefined {
  const tags = Array.from(item.querySelectorAll('category'))
    .map((category) => (category.getAttribute('term') || category.textContent || '').trim())
    .filter(Boolean)
  return tags.length > 0 ? tags : undefined
}

function parseRssDate(dateStr: string): Date {
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? new Date() : date
}

function parseRss2Item(item: Element, platform: RssPlatform): RssArticle | null {
  const title = item.querySelector('title')?.textContent
  const link = item.querySelector('link')?.textContent
  const pubDate = item.querySelector('pubDate')?.textContent
  const description = item.querySelector('description')?.textContent || ''
  const content = item.querySelector('content\\:encoded')?.textContent || ''
  const guid = item.querySelector('guid')?.textContent

  if (!title || !link) return null

  return {
    id: guid || link,
    title: stripHtmlTags(title),
    description: stripHtmlTags(description).slice(0, 200),
    url: link,
    publishedAt: parseRssDate(pubDate || ''),
    platform,
    thumbnail: extractThumbnail(content) || extractThumbnail(description),
    tags: extractTags(item),
  }
}

function parseAtomEntry(entry: Element, platform: RssPlatform): RssArticle | null {
  const title = entry.querySelector('title')?.textContent
  const linkEl = entry.querySelector('link[rel="alternate"]') || entry.querySelector('link')
  const link = linkEl?.getAttribute('href')
  const published = entry.querySelector('published')?.textContent || entry.querySelector('updated')?.textContent
  const summary = entry.querySelector('summary')?.textContent || ''
  const content = entry.querySelector('content')?.textContent || ''
  const id = entry.querySelector('id')?.textContent

  if (!title || !link) return null

  return {
    id: id || link,
    title: stripHtmlTags(title),
    description: stripHtmlTags(summary || content).slice(0, 200),
    url: link,
    publishedAt: parseRssDate(published || ''),
    platform,
    thumbnail: extractThumbnail(content) || extractThumbnail(summary),
    tags: extractTags(entry),
  }
}

function parseXmlFeed(xmlText: string, platform: RssPlatform): RssArticle[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlText, 'text/xml')

  const parserError = doc.querySelector('parsererror')
  if (parserError) {
    console.error(`XML parse error for ${platform}:`, parserError.textContent)
    return []
  }

  const articles: RssArticle[] = []

  const rssItems = doc.querySelectorAll('item')
  if (rssItems.length > 0) {
    rssItems.forEach((item) => {
      const article = parseRss2Item(item, platform)
      if (article) articles.push(article)
    })
  } else {
    const atomEntries = doc.querySelectorAll('entry')
    atomEntries.forEach((entry) => {
      const article = parseAtomEntry(entry, platform)
      if (article) articles.push(article)
    })
  }

  return articles.slice(0, ITEMS_PER_FEED)
}

declare global {
  interface Window {
    __rssPrefetch?: Record<string, Promise<string> | undefined>
  }
}

async function fetchFeedXml(platform: RssPlatform): Promise<string> {
  // Reuse the request the inline <head> script started before React mounted.
  const prefetched =
    typeof window !== 'undefined' ? window.__rssPrefetch?.[platform] : undefined

  if (prefetched) {
    // Consume once so a later refetch() goes to the network for fresh data.
    if (window.__rssPrefetch) window.__rssPrefetch[platform] = undefined
    try {
      return await prefetched
    } catch {
      // Prefetch failed — fall through to a live fetch below.
    }
  }

  const response = await fetch(`/api/rss?platform=${platform}`)
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`)
  }
  return response.text()
}

async function fetchFeed(platform: RssPlatform): Promise<RssArticle[]> {
  try {
    const xmlContent = await fetchFeedXml(platform)
    return parseXmlFeed(xmlContent, platform)
  } catch (error) {
    console.error(`Failed to fetch ${platform} feed:`, error)
    return []
  }
}

export async function fetchAllRssFeeds(): Promise<RssArticle[]> {
  const platforms: RssPlatform[] = ['zenn', 'medium']

  const results = await Promise.all(platforms.map((platform) => fetchFeed(platform)))

  const allArticles = results.flat()
  allArticles.sort((a, b) => b.publishedAt.getTime() - a.publishedAt.getTime())

  return allArticles
}
